import { useCallback, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { useToast } from '@/hooks/useToast';
import { MEDIA_ACCEPT, type MediaItem } from '@/lib/media';
import { cn } from '@/lib/utils';

interface MediaDropZoneProps {
  /** Called for each file successfully added, so callers can auto-select or insert it. */
  onAdded?: (item: MediaItem) => void;
  className?: string;
  children?: ReactNode;
}

/**
 * Drag-and-drop / click / paste target that adds files to the local
 * media library.
 *
 * Adding is deliberately **local-only** — files land in IndexedDB and are
 * not uploaded here. That's what lets the whole media workflow function
 * offline and while logged out; uploading is a separate, explicit action
 * (or happens automatically at publish time).
 */
export function MediaDropZone({ onAdded, className, children }: MediaDropZoneProps) {
  const { add } = useMediaLibrary();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = [...files];
      if (list.length === 0) return;

      setBusy(true);
      let added = 0;
      let duplicates = 0;

      for (const file of list) {
        try {
          const result = await add(file);
          if (result.duplicate) duplicates += 1;
          else added += 1;
          onAdded?.(result.item);
        } catch (error) {
          toast({
            title: `Couldn't add "${file.name}"`,
            description: error instanceof Error ? error.message : 'Something went wrong.',
            variant: 'destructive',
          });
        }
      }

      setBusy(false);

      if (added > 0 || duplicates > 0) {
        toast({
          title: added > 0 ? `Added ${added} file${added === 1 ? '' : 's'}` : 'Already in your library',
          description:
            duplicates > 0 && added > 0
              ? `${duplicates} ${duplicates === 1 ? 'was' : 'were'} already in your library.`
              : duplicates > 0
                ? 'These files are already saved on this device.'
                : 'Saved on this device. Upload them, or let publishing handle it.',
        });
      }
    },
    [add, onAdded, toast],
  );

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsOver(false);
    if (e.dataTransfer?.files?.length) void handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      onPaste={(e) => {
        const files = [...(e.clipboardData?.files ?? [])];
        if (files.length) void handleFiles(files);
      }}
      className={cn(
        'rounded-lg border-2 border-dashed transition-colors',
        isOver ? 'border-primary bg-primary/5' : 'border-border',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={MEDIA_ACCEPT}
        multiple
        className="sr-only"
        onChange={(e) => {
          if (e.target.files) void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="flex w-full flex-col items-center gap-2 px-6 py-8 text-center"
      >
        {busy ? (
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        ) : (
          <Upload className="size-6 text-muted-foreground" />
        )}
        {children ?? (
          <>
            <span className="text-sm font-medium">
              {busy ? 'Adding files…' : 'Drop files here, paste, or click to choose'}
            </span>
            <span className="text-xs text-muted-foreground">
              Images, video, audio, and PDFs. Saved to this device first — no connection needed.
            </span>
          </>
        )}
      </button>
    </div>
  );
}
